import apiClient from '@/services/apiClient';
import { downloadCsv } from '@/services/transportApi';

const E = '/eway-bills';

export const ewbApi = {
  config: () => apiClient.get(`${E}/config`).then((r) => r.data),
  saveUnits: (units) => apiClient.put(`${E}/settings/units`, { units }).then((r) => r.data),
  documents: (params) => apiClient.get(`${E}/documents`, { params }).then((r) => r.data.documents),
  transporters: () => apiClient.get(`${E}/transporters`).then((r) => r.data.transporters),
  transfersToShip: (params) => apiClient.get(`${E}/transfers-to-ship`, { params }).then((r) => r.data.rows),
  trips: () => apiClient.get(`${E}/trips`).then((r) => r.data.trips),
  draft: (body) => apiClient.post(`${E}/draft`, body).then((r) => r.data),
  generate: (body) => apiClient.post(E, body).then((r) => r.data),
  list: (params) => apiClient.get(E, { params }).then((r) => r.data.bills),
  get: (id) => apiClient.get(`${E}/${id}`).then((r) => r.data),
  cancel: (id, body) => apiClient.post(`${E}/${id}/cancel`, body).then((r) => r.data),
  fixDistance: (id, km) => apiClient.post(`${E}/${id}/distance`, { distance_km: km }).then((r) => r.data),
  keyEwbNo: (id, body) => apiClient.post(`${E}/${id}/ewb-no`, body).then((r) => r.data),
  tallyWaiting: () => apiClient.get(`${E}/tally-waiting`).then((r) => r.data),
  deleteTallyWaiting: () => apiClient.post(`${E}/tally-waiting/delete`).then((r) => r.data),
  gateRule: () => apiClient.get(`${E}/gate-rule`).then((r) => r.data),
  setGateRule: (enforce) => apiClient.put(`${E}/gate-rule`, { enforce }).then((r) => r.data),
  tallyPush: () => apiClient.get(`${E}/tally-push`).then((r) => r.data),
  setTallyPush: (enforce) => apiClient.put(`${E}/tally-push`, { enforce }).then((r) => r.data),
  portalImport: (file) => {
    const f = new FormData();
    f.append('file', file);
    return apiClient.post(`${E}/portal-import`, f, { headers: { 'Content-Type': undefined } }).then((r) => r.data);
  },
  portalJson: async (ids) => {
    const r = await apiClient.post(`${E}/portal-json`, { ids }, { responseType: 'blob' });
    const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'ewb_portal.json';
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return Number(r.headers['x-bill-count'] || ids.length);
  },
  bulkPdf: async (ids) => {
    const r = await apiClient.post(`${E}/pdf-bulk`, { ids }, { responseType: 'blob' });
    const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'eway_bills.pdf';
    return { blob: new Blob([r.data], { type: 'application/pdf' }), name, count: Number(r.headers['x-bill-count'] || ids.length) };
  },
};

const D = '/ewb-docs';
const upload = (url, file) => {
  const f = new FormData();
  f.append('file', file);
  return apiClient.post(url, f, { headers: { 'Content-Type': undefined } }).then((r) => r.data);
};
export const ewbDocsApi = {
  uploadInvoices: (file) => upload(`${D}/invoices/upload`, file),
  uploadTransferOut: (file) => upload(`${D}/transfer-out/upload`, file),
  uploadOutsideInvoices: (file) => upload(`${D}/outside-invoices/upload`, file),
  manualExtract: (file) => upload(`${D}/manual/extract`, file),
  manualStatus: (id) => apiClient.get(`${D}/manual/extract/${id}`).then((r) => r.data),
  manualFileUrl: (id) => apiClient.get(`${D}/manual/upload/${id}/file`, { responseType: 'blob' }).then((r) => URL.createObjectURL(r.data)),
  saveManual: (body) => apiClient.post(`${D}/manual`, body).then((r) => r.data),
  getManual: (docNo) => apiClient.get(`${D}/manual/doc`, { params: { doc_no: docNo } }).then((r) => r.data),
  destinations: (toSection) => apiClient.get(`${D}/destinations`, { params: { to_section: toSection || '' } }).then((r) => r.data),
};

const I = '/ewb-inward';
export const ewbInwardApi = {
  extract: (file) => upload(`${I}/extract`, file),
  parties: (q) => apiClient.get(`${I}/parties`, { params: { q } }).then((r) => r.data.parties),
  save: (body) => apiClient.post(I, body).then((r) => r.data),
  get: (id) => apiClient.get(`${I}/doc/${id}`).then((r) => r.data),
  suppliers: (q) => apiClient.get(`${I}/suppliers`, { params: { q } }).then((r) => r.data),
  addSupplier: (body) => apiClient.post(`${I}/suppliers`, body).then((r) => r.data),
  saveSupplier: (id, body) => apiClient.put(`${I}/suppliers/${id}`, body).then((r) => r.data),
  mergeSuppliers: (body) => apiClient.post(`${I}/suppliers/merge`, body).then((r) => r.data),
};

const H = '/ewb-holds';
export const holdsApi = {
  list: () => apiClient.get(H).then((r) => r.data),
  count: () => apiClient.get(`${H}/count`).then((r) => r.data.count),
  recheck: (body) => apiClient.post(`${H}/recheck`, body).then((r) => r.data),
  recheckAll: () => apiClient.post(`${H}/recheck-all`).then((r) => r.data),
  keyEwb: (body) => apiClient.post(`${H}/key-ewb`, body).then((r) => r.data),
  run: (id) => apiClient.get(`${H}/runs/${id}`).then((r) => r.data),
};

export const ewbPdf = (b) => downloadCsv(`${E}/${b.id}/pdf`, `eway_bill_${b.ewb_no}.pdf`);

const S = '/ewb-direct-ship';
export const directShipApi = {
  upload: (file) => upload(`${S}/transfer-out/upload`, file),
  list: (q = '') => apiClient.get(`${S}/transfer-outs`, { params: { q } }).then((r) => r.data.transfer_outs),
  boxes: (toId) => apiClient.get(`${S}/boxes`, { params: { to_id: toId } }).then((r) => r.data.sites),
  setBoxes: (toId, boxNumbers) => apiClient.put(`${S}/transfer-outs/${toId}/boxes`, { box_numbers: boxNumbers }).then((r) => r.data),
  remove: (toId) => apiClient.delete(`${S}/transfer-outs/${toId}`).then((r) => r.data),
  pending: (days = 15) => apiClient.get(`${S}/pending`, { params: { days } }).then((r) => r.data),
};

const T = '/tally-bridge';
export const tallyApi = {
  status: () => apiClient.get(`${T}/status`).then((r) => r.data),
  saveSettings: (body) => apiClient.put(`${T}/settings`, body).then((r) => r.data),
  syncMasters: () => apiClient.post(`${T}/masters/sync`).then((r) => r.data),
  masters: (params) => apiClient.get(`${T}/masters`, { params }).then((r) => r.data.names),
  mapName: (body) => apiClient.put(`${T}/name-map`, body).then((r) => r.data),
  gstinParties: (days) => apiClient.get(`${T}/gstin-parties`, { params: { days } }).then((r) => r.data),
  gstinXlsx: (days) => downloadCsv(`${T}/gstin-parties/xlsx?days=${days}`, `tally_gstin_parties_${days}d.xlsx`),
  retry: (id) => apiClient.post(`${E}/${id}/retry`).then((r) => r.data),
  discard: (id) => apiClient.post(`${E}/${id}/discard`).then((r) => r.data),
  downloadScript: async (wmsUrl) => {
    const r = await apiClient.post(`${T}/agent-script`, { wms_url: wmsUrl }, { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = Object.assign(document.createElement('a'), { href: url, download: 'wms_tally_link.py' });
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
